/**
 * Tenant directory store — folders/collections/policy library via API (DB),
 * with one-time localStorage migration. Auth token stays in localStorage.
 */
import { apiService } from '../services/api';
import {
  STORAGE_FOLDERS,
  STORAGE_COLLECTIONS,
  STORAGE_POLICY_LIBRARY,
} from '../constants/storageKeys';

export const DIRECTORY_UPDATED_EVENT = 'uem-directory-updated';
export const UNASSIGNED_COLLECTION_ID = '__unassigned__';
export const DIRECTORY_ROOT_ID = '__directory__';

const MIGRATED_FLAG_PREFIX = 'uem_directory_migrated_to_db';

export type DirectoryFolder = {
  id: string;
  name: string;
  isDefault?: boolean;
  createdAt?: string;
};

export type DirectoryCollection = {
  id: string;
  name: string;
  folderId?: string | null;
  deviceIds?: string[];
  criticalDeviceIds?: string[];
  createdAt?: string;
};

export type PolicyLibrary = {
  general: Record<string, unknown>;
  baselines: Record<string, unknown>;
};

/** Scope browser cache by tenant so Varna never shows Nutrinium folders from a prior login. */
function currentTenantScope(): string {
  try {
    const raw = localStorage.getItem('uem_user');
    if (!raw) return 'anon';
    const u = JSON.parse(raw);
    return String(u?.tenant_id || u?.tenant_code || 'anon');
  } catch {
    return 'anon';
  }
}

function foldersKey(): string {
  return `${STORAGE_FOLDERS}:${currentTenantScope()}`;
}
function collectionsKey(): string {
  return `${STORAGE_COLLECTIONS}:${currentTenantScope()}`;
}
function policyKey(): string {
  return `${STORAGE_POLICY_LIBRARY}:${currentTenantScope()}`;
}
function migratedFlagKey(): string {
  return `${MIGRATED_FLAG_PREFIX}:${currentTenantScope()}`;
}

function isNutriniumTenant(): boolean {
  try {
    const raw = localStorage.getItem('uem_user');
    if (!raw) return false;
    const u = JSON.parse(raw);
    const code = String(u?.tenant_code || '').toUpperCase();
    const name = String(u?.tenant_name || '').toLowerCase();
    return code === 'NUTRINIUM' || name === 'nutrinium';
  } catch {
    return false;
  }
}

/** Drop seed-tenant brand folders when importing into another client. */
function sanitizeLocalForTenant(
  folders: DirectoryFolder[],
  collections: DirectoryCollection[]
): { folders: DirectoryFolder[]; collections: DirectoryCollection[] } {
  if (isNutriniumTenant()) return { folders, collections };
  const brandFolderIds = new Set(['folder-nutrinium']);
  const brandCollIds = new Set(['coll-aws-servers', 'coll-bi']);
  const cleanedFolders = folders.filter(
    (f) => !brandFolderIds.has(f.id) && String(f.name).toLowerCase() !== 'nutrinium'
  );
  const cleanedCollections = collections.filter(
    (c) =>
      !brandCollIds.has(c.id) &&
      !brandFolderIds.has(String(c.folderId || '')) &&
      !['AWS servers', 'BI'].includes(String(c.name))
  );
  return { folders: cleanedFolders, collections: cleanedCollections };
}

function notifyDirectoryUpdated() {
  try {
    window.dispatchEvent(new CustomEvent(DIRECTORY_UPDATED_EVENT));
  } catch {
    /* ignore */
  }
}

function readLocalFolders(): DirectoryFolder[] {
  try {
    const raw = localStorage.getItem(foldersKey()) || localStorage.getItem(STORAGE_FOLDERS);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readLocalCollections(): DirectoryCollection[] {
  try {
    const raw = localStorage.getItem(collectionsKey()) || localStorage.getItem(STORAGE_COLLECTIONS);
    const parsed = raw ? JSON.parse(raw) : [];
    return (Array.isArray(parsed) ? parsed : []).map((c: any) => ({
      ...c,
      deviceIds: Array.isArray(c.deviceIds) ? c.deviceIds : [],
      criticalDeviceIds: Array.isArray(c.criticalDeviceIds) ? c.criticalDeviceIds : [],
    }));
  } catch {
    return [];
  }
}

function readLocalPolicyLibrary(): PolicyLibrary {
  try {
    const raw = localStorage.getItem(policyKey()) || localStorage.getItem(STORAGE_POLICY_LIBRARY);
    const parsed = raw ? JSON.parse(raw) : { general: {}, baselines: {} };
    return {
      general: parsed?.general || {},
      baselines: parsed?.baselines || {},
    };
  } catch {
    return { general: {}, baselines: {} };
  }
}

/** Normalize API folder shape (isDefault) to UI shape */
function normalizeFolders(folders: any[]): DirectoryFolder[] {
  return (folders || []).map((f) => ({
    id: f.id,
    name: f.name,
    isDefault: !!(f.isDefault ?? f.is_default),
    createdAt: f.createdAt || f.created_at,
  }));
}

function normalizeCollections(collections: any[]): DirectoryCollection[] {
  return (collections || []).map((c) => ({
    id: c.id,
    name: c.name,
    folderId: c.folderId ?? c.folder_id ?? null,
    deviceIds: Array.isArray(c.deviceIds) ? c.deviceIds : Array.isArray(c.device_ids) ? c.device_ids : [],
    criticalDeviceIds: Array.isArray(c.criticalDeviceIds)
      ? c.criticalDeviceIds
      : Array.isArray(c.critical_device_ids)
        ? c.critical_device_ids
        : [],
    createdAt: c.createdAt || c.created_at,
  }));
}

/**
 * One-time: push browser localStorage dump into DB if not yet migrated (per tenant).
 */
export async function migrateLocalDirectoryIfNeeded(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (localStorage.getItem(migratedFlagKey()) === '1') return;

  const local = sanitizeLocalForTenant(readLocalFolders(), readLocalCollections());
  const folders = local.folders;
  const collections = local.collections;
  const policy_library = readLocalPolicyLibrary();
  const hasLocal =
    folders.length > 0 ||
    collections.length > 0 ||
    Object.keys(policy_library.general || {}).length > 0 ||
    Object.keys(policy_library.baselines || {}).length > 0;

  try {
    if (hasLocal) {
      await apiService.importTenantDirectory({
        folders: folders.map((f) => ({
          id: String(f.id ?? ''),
          name: String(f.name ?? f.id ?? ''),
          isDefault: !!f.isDefault,
        })),
        collections: collections.map((c) => ({
          id: String(c.id ?? ''),
          name: String(c.name ?? c.id ?? ''),
          folderId: c.folderId != null ? String(c.folderId) : null,
          deviceIds: (c.deviceIds || []).map((id) => String(id)),
          criticalDeviceIds: (c.criticalDeviceIds || []).map((id) => String(id)),
          createdAt: c.createdAt,
        })),
        policy_library: {
          general:
            policy_library &&
            typeof policy_library.general === 'object' &&
            !Array.isArray(policy_library.general)
              ? policy_library.general
              : {},
          baselines:
            policy_library &&
            typeof policy_library.baselines === 'object' &&
            !Array.isArray(policy_library.baselines)
              ? policy_library.baselines
              : {},
        },
        replace: false,
      });
    }
    localStorage.setItem(migratedFlagKey(), '1');
  } catch (e) {
    console.warn('[directoryStore] migrate skipped:', e);
  }
}

function writeMirror(folders: DirectoryFolder[], collections: DirectoryCollection[]) {
  try {
    localStorage.setItem(foldersKey(), JSON.stringify(folders));
    localStorage.setItem(collectionsKey(), JSON.stringify(collections));
    // Clear legacy global keys so sidebar mirror does not show another tenant
    localStorage.removeItem(STORAGE_FOLDERS);
    localStorage.removeItem(STORAGE_COLLECTIONS);
  } catch {
    /* ignore */
  }
}

export async function loadDirectory(): Promise<{
  folders: DirectoryFolder[];
  collections: DirectoryCollection[];
}> {
  await migrateLocalDirectoryIfNeeded();
  const data = await apiService.getTenantDirectory();
  const result = {
    folders: normalizeFolders(data?.folders || []),
    collections: normalizeCollections(data?.collections || []),
  };
  writeMirror(result.folders, result.collections);
  notifyDirectoryUpdated();
  return result;
}

export async function saveDirectory(
  folders: DirectoryFolder[],
  collections: DirectoryCollection[]
): Promise<{ folders: DirectoryFolder[]; collections: DirectoryCollection[] }> {
  const data = await apiService.putTenantDirectory({
    folders: folders.map((f) => ({
      id: f.id,
      name: f.name,
      isDefault: !!f.isDefault,
    })),
    collections: collections.map((c) => ({
      id: c.id,
      name: c.name,
      folderId: c.folderId ?? null,
      deviceIds: c.deviceIds || [],
      criticalDeviceIds: c.criticalDeviceIds || [],
      createdAt: c.createdAt,
    })),
  });
  const result = {
    folders: normalizeFolders(data?.folders || folders),
    collections: normalizeCollections(data?.collections || collections),
  };
  writeMirror(result.folders, result.collections);
  notifyDirectoryUpdated();
  return result;
}

export async function saveFolders(folders: DirectoryFolder[]): Promise<DirectoryFolder[]> {
  const current = await loadDirectory();
  const saved = await saveDirectory(folders, current.collections);
  return saved.folders;
}

export async function saveCollections(
  collections: DirectoryCollection[]
): Promise<DirectoryCollection[]> {
  const current = await loadDirectory();
  const saved = await saveDirectory(current.folders, collections);
  return saved.collections;
}

export async function loadPolicyLibrary(): Promise<PolicyLibrary> {
  await migrateLocalDirectoryIfNeeded();
  try {
    const data = await apiService.getTenantPolicyLibrary();
    return {
      general: data?.general || {},
      baselines: data?.baselines || {},
    };
  } catch {
    return readLocalPolicyLibrary();
  }
}

export async function savePolicyLibrary(lib: PolicyLibrary): Promise<PolicyLibrary> {
  const general =
    lib && typeof lib.general === 'object' && !Array.isArray(lib.general) ? lib.general : {};
  const baselines =
    lib && typeof lib.baselines === 'object' && !Array.isArray(lib.baselines) ? lib.baselines : {};
  const data = await apiService.putTenantPolicyLibrary({ general, baselines });
  const result = {
    general: data?.general || lib.general || {},
    baselines: data?.baselines || lib.baselines || {},
  };
  try {
    localStorage.setItem(policyKey(), JSON.stringify(result));
    localStorage.removeItem(STORAGE_POLICY_LIBRARY);
  } catch {
    /* ignore */
  }
  return result;
}

/**
 * Seed default folders/collections on the server if empty (API also seeds).
 * Kept for callers that previously used seedDefaultDirectory().
 */
export async function seedDefaultDirectory(): Promise<void> {
  try {
    await loadDirectory();
  } catch (e) {
    console.warn('[directoryStore] seedDefaultDirectory failed:', e);
  }
}
